import { useCallback } from 'react'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../../common/firebaseConfig'

export type CreateAlertFromTemplateResult = {
  status: number
  message?: string
  outcome?: 'created' | 'exists'
  alertId?: string
  // Set when outcome is 'created': whether the new alert went live, and if not, why.
  active?: boolean
  inactiveReason?: 'activeLimit' | 'customIndicators'
}

/**
 * Creates one alert from a standard alert template, switched on when the member has room
 * for another active alert.
 *
 * Server-side (firebase-functions createAlertFromTemplate) because the tradingPlans rules
 * validate only user.gcid: the callable is what strips auto-trading, checks the active-alert
 * cap, and stamps alertTemplateId so template edits keep reaching the alert.
 *
 * useHttpsCallable resolves undefined on failure rather than rejecting, so a missing
 * result is surfaced as an error rather than read as success.
 */
export function useCreateAlertFromTemplate() {
  const [callable] = useHttpsCallable(firebase_functions, 'createAlertFromTemplate')

  return useCallback(
    async (templateId: string, tradingHouse: string): Promise<CreateAlertFromTemplateResult> => {
      const result = await callable({ templateId, tradingHouse })
      const data = result?.data as CreateAlertFromTemplateResult | undefined

      if (!data) {
        return { status: 500, message: 'Could not activate the alert. Please try again.' }
      }

      return data
    },
    [callable]
  )
}
